import { formatShortDate } from '../util/formatters';
// Apaleo's `minGuaranteeType` is the closest signal to a payment expectation.
// Unmapped values fall back to a readable form of the raw value.
const PAYMENT_TERMS = {
    Prepayment: 'Prepaid',
    Company: 'Bill to company',
    CreditCard: 'Card guarantee',
    PM6Hold: 'Hold until 6 PM',
    NoGuarantee: 'No guarantee'
};
const readablePaymentTerms = (guaranteeType) => {
    if (!guaranteeType) {
        return 'Pay at property';
    }
    return (PAYMENT_TERMS[guaranteeType] ??
        guaranteeType.replace(/([a-z])([A-Z])/g, '$1 $2'));
};
// No fee -> fully refundable. A fee with a due date -> free until that date.
// A fee without a due date -> the penalty applies from the outset.
const cancellationTerms = (offer) => {
    const fee = offer.cancellationFee;
    if (!fee) {
        return 'Free cancellation';
    }
    if (fee.dueDateTime) {
        return `Free cancellation until ${formatShortDate(fee.dueDateTime)}`;
    }
    return 'Non-refundable';
};
const stayNights = (offer) => offer.timeSlices.length;
const stayTotal = (offer) => offer.totalGrossAmount?.amount ??
    offer.timeSlices.reduce((sum, slice) => sum + slice.baseAmount.grossAmount, 0);
const offerCurrency = (offer) => offer.totalGrossAmount?.currency ??
    offer.timeSlices[0]?.baseAmount.currency ??
    'USD';
// The average nightly room rate across the stay. Averaging (rather than showing
// the first night) keeps the "/night" figure honest when nightly rates vary.
const nightlyAmount = (offer) => {
    const nights = stayNights(offer);
    if (nights === 0) {
        return stayTotal(offer);
    }
    const baseSum = offer.timeSlices.reduce((sum, slice) => sum + slice.baseAmount.grossAmount, 0);
    return baseSum / nights;
};
// Raw Apaleo pricing, summed as-carried — no repricing. Taxes are already
// contained within `totalGrossAmount`; fees (`OfferFeeModel.totalAmount`) are
// added on top of it, per the Apaleo offers spec.
const taxesTotal = (offer) => (offer.taxDetails ?? []).reduce((sum, detail) => sum + detail.tax.amount, 0);
const feesTotal = (offer) => (offer.fees ?? []).reduce((sum, fee) => sum + fee.totalAmount.grossAmount, 0);
// The stay grand total the salesperson quotes: room + taxes (in
// `totalGrossAmount`) plus any fees added on top.
const grandTotal = (offer) => stayTotal(offer) + feesTotal(offer);
// Taxes (within the total) plus fees (on top of it).
const taxesAndFeesAmount = (offer) => taxesTotal(offer) + feesTotal(offer);
// Room charges are the pre-tax remainder of `totalGrossAmount`, so that
// room + taxes & fees always reconciles to the grand total.
const roomChargesAmount = (offer) => stayTotal(offer) - taxesTotal(offer);
export const toRatePlanView = (offer) => ({
    ratePlanId: offer.ratePlan?.id ?? offer.ratePlan?.name ?? 'rate-plan',
    ratePlanName: offer.ratePlan?.name ?? 'Rate',
    paymentTerms: readablePaymentTerms(offer.minGuaranteeType),
    cancellationTerms: cancellationTerms(offer),
    nightlyAmount: nightlyAmount(offer),
    totalAmount: grandTotal(offer),
    currency: offerCurrency(offer),
    availableUnits: offer.availableUnits,
    isRestricted: offer.isRestricted,
    restrictionMessages: (offer.validationMessages ?? []).map(m => m.message),
    arrival: offer.arrival,
    departure: offer.departure,
    nights: stayNights(offer),
    roomChargesAmount: roomChargesAmount(offer),
    taxesAndFeesAmount: taxesAndFeesAmount(offer)
});
export const DEFAULT_OFFER_FILTERS = {
    hideRestricted: true,
    hideUnavailable: true
};
// Drops restricted rate plans (when enabled), recomputing the room's
// availability from what remains so an all-restricted room reads as sold out.
const filterRatePlans = (group, hideRestricted) => {
    if (!hideRestricted) {
        return group;
    }
    const ratePlans = group.ratePlans.filter(plan => !plan.isRestricted);
    return {
        ...group,
        ratePlans,
        availableUnits: ratePlans.length > 0
            ? Math.max(...ratePlans.map(plan => plan.availableUnits))
            : 0
    };
};
// Applies the offer filters to grouped offers: hide restricted rates, drop
// rooms left with no rate plans, and hide sold-out rooms. Pure — the UI holds
// the toggle state and passes it in.
export const applyOfferFilters = (groups, filters) => groups
    .map(group => filterRatePlans(group, filters.hideRestricted))
    .filter(group => group.ratePlans.length > 0)
    .filter(group => !filters.hideUnavailable || group.availableUnits > 0);
const roomTypeKey = (offer) => offer.unitGroup?.id ?? offer.unitGroup?.name ?? 'unknown';
// Groups offers into room types, preserving the order each room type first
// appears and the order of rate plans within it. Room-level availability is the
// max across the room's rate plans.
export const groupOffersByRoomType = (offers) => {
    const order = [];
    const byRoom = new Map();
    offers.forEach(offer => {
        const key = roomTypeKey(offer);
        const existing = byRoom.get(key);
        if (existing) {
            byRoom.set(key, [...existing, offer]);
        }
        else {
            byRoom.set(key, [offer]);
            order.push(key);
        }
    });
    return order.map(key => {
        const roomOffers = byRoom.get(key) ?? [];
        const first = roomOffers[0];
        return {
            roomTypeId: key,
            roomTypeName: first?.unitGroup?.name ?? 'Room',
            availableUnits: Math.max(...roomOffers.map(o => o.availableUnits)),
            ratePlans: roomOffers.map(toRatePlanView)
        };
    });
};
